import { useOrgContext, useOrgEditActions } from '@/org/contexts/OrgContext'
import { RoleFragment, useCreateMeetingTemplateMutation } from '@gql'
import { MeetingStepConfig } from '@rolebase/shared/model/meeting'
import { nanoid } from 'nanoid'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { creatorNeedsRootSeat } from '../creatorNeedsRootSeat'
import { OrgType, orgTypePresets } from '../orgTypes'

export interface SeedRoleInput {
  // Draft id from the setup modal, so two roles with the same name stay apart
  id: string
  name: string
  responsibleId?: string
  participantIds: string[]
}

export interface SeedOrgInput {
  orgType: OrgType
  rootCircleId: string
  roles: SeedRoleInput[]
  // Seated in the root circle when they end up holding none of the roles
  creatorMemberId?: string
  progress: SeedOrgProgress
}

// What a previous, interrupted run already wrote. Seeding is a chain of
// separate writes with no transaction: when one fails, the earlier ones stay.
// A second click must resume from here instead of starting over, or it
// duplicates base roles, circles and templates (seen on dev and UdN, 21/09).
// Every write is recorded right after it succeeds.
export interface SeedOrgProgress {
  baseRoles: Record<string, RoleFragment | true> // by nameKey
  circles: Record<string, string> // circle id, by `${draftId}` or `${draftId}:lead`
  members: Record<string, true> // by `${circleId}:${memberId}`
  templates: Record<string, true> // by titleKey
}

export const createSeedOrgProgress = (): SeedOrgProgress => ({
  baseRoles: {},
  circles: {},
  members: {},
  templates: {},
})

export default function useSeedOrg() {
  const { t } = useTranslation()
  const { orgId } = useOrgContext()
  const { createCircle, createRole, addCircleMember } = useOrgEditActions()
  const [createMeetingTemplate] = useCreateMeetingTemplateMutation()

  return useCallback(
    async ({
      orgType,
      rootCircleId,
      roles,
      creatorMemberId,
      progress,
    }: SeedOrgInput): Promise<void> => {
      if (!orgId) return
      const preset = orgTypePresets[orgType]

      // Create the model's base governance roles once (shared across circles).
      // Keep the first parent-linking role as each circle's representation role.
      let leaderRole: RoleFragment | undefined
      for (const baseRole of preset.baseRoles) {
        let role = progress.baseRoles[baseRole.nameKey]
        if (!role) {
          role =
            (await createRole({
              name: t(`Onboarding.roles.${baseRole.nameKey}.name`),
              base: true,
              singleMember: true,
              parentLink: baseRole.parentLink ?? false,
              colorHue: baseRole.colorHue,
              purpose: t(`Onboarding.roles.${baseRole.nameKey}.purpose`),
              domain: t(`Onboarding.roles.${baseRole.nameKey}.domain`),
              accountabilities: t(
                `Onboarding.roles.${baseRole.nameKey}.accountabilities`
              ),
            })) ?? true
          progress.baseRoles[baseRole.nameKey] = role
        }
        if (role !== true && baseRole.parentLink && !leaderRole) {
          leaderRole = role
        }
      }

      const ensureCircle = async (
        key: string,
        parentId: string,
        roleOrName: RoleFragment | string
      ) => {
        if (!progress.circles[key]) {
          const circleId = await createCircle(parentId, roleOrName)
          if (!circleId) return
          progress.circles[key] = circleId
        }
        return progress.circles[key]
      }
      const ensureMember = async (circleId: string, memberId: string) => {
        const key = `${circleId}:${memberId}`
        if (progress.members[key]) return
        await addCircleMember(circleId, memberId)
        progress.members[key] = true
      }

      // One circle per main role under the root
      for (const role of roles) {
        const circleId = await ensureCircle(role.id, rootCircleId, role.name)
        if (!circleId) continue

        if (role.participantIds.length === 0) {
          // No one else: the person responsible occupies the role directly
          if (role.responsibleId) {
            await ensureMember(circleId, role.responsibleId)
          }
        } else {
          // The person responsible leads the circle via the model's leader role
          // (a parent-linking sub-circle); the others are direct members.
          if (role.responsibleId && leaderRole) {
            const leadCircleId = await ensureCircle(
              `${role.id}:lead`,
              circleId,
              leaderRole
            )
            if (leadCircleId) {
              await ensureMember(leadCircleId, role.responsibleId)
            }
          }
          for (const participantId of role.participantIds) {
            await ensureMember(circleId, participantId)
          }
        }
      }

      // Meeting templates only: nobody's calendar is filled with dates they
      // didn't choose.
      for (const template of preset.meetingTemplates) {
        const key = template.titleKey
        if (progress.templates[key]) continue
        const stepsConfig: MeetingStepConfig[] = template.steps.map((type) => ({
          id: nanoid(8),
          type,
          title: t(`common.meetingSteps.${type}`),
        }))
        await createMeetingTemplate({
          variables: {
            values: {
              orgId,
              title: t(`Onboarding.meetings.${template.titleKey}`),
              stepsConfig,
            },
          },
        })
        progress.templates[key] = true
      }

      // Last: for an empty org, this seat is also what ends the setup
      // (see isOrgFresh), so it must follow every other write.
      if (creatorMemberId && creatorNeedsRootSeat(roles, creatorMemberId)) {
        await ensureMember(rootCircleId, creatorMemberId)
      }
    },
    [orgId, t, createCircle, createRole, addCircleMember, createMeetingTemplate]
  )
}
